import {
  VStack,
  Flex,
  Heading,
  Text,
  Skeleton,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { ValleyBankHeader } from './ValleyBankHeader';
import { ValleyBankSideBar } from './ValleyBankSideBar';
import { Alert } from 'packages/common-frontend/components/Alert';
import { errorToastConfig } from 'packages/common-frontend/constants';
import CreditFacilityProvider from 'packages/credit-facility/hooks/CreditFacilityProvider';
import CreditFacilityMissingFallback from 'packages/credit-facility/ui/CreditFacilityMissingFallback';
import { HEADER_HEIGHT } from 'packages/header/ui/Header';
import { DataMetadataContextType } from 'valleyBank/domains/page/hooks/DataMetadataContextType';
import { ValleyBankDataMedataHeader } from 'valleyBank/domains/page/ui/ValleyBankDataMedataHeader';

type ValleyBankCalcAgentErrorFallbackPageProps = {
  errors: string[];
  context: DataMetadataContextType;
  isAssignmentEnabled: boolean;
  isAdvanceRateEnabled: boolean;
};
export const ValleyBankCalcAgentErrorFallbackPage = ({
  errors,
  context,
  isAdvanceRateEnabled,
  isAssignmentEnabled,
}: ValleyBankCalcAgentErrorFallbackPageProps) => {
  const toast = useToast();
  const { isPreview } = context;
  const { data, loading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (!data || loading) {
    return (
      <Skeleton data-testid="loading-capital-provider-router" height="600px" />
    );
  }

  const { user } = data;

  finleyAssert(user, 'User expected to exist');

  return (
    <>
      <ValleyBankHeader />
      <ValleyBankDataMedataHeader context={context} />
      <Flex minH="95vh" grow={1} pt={`${HEADER_HEIGHT}px`}>
        <CreditFacilityProvider
          organizationId={user.organizationId}
          renderLoading={<Skeleton />}
          fallbackComponent={<CreditFacilityMissingFallback />}
        >
          <ValleyBankSideBar
            canToggleDataSource={false}
            isAdvanceRateEnabled={isAdvanceRateEnabled}
            isAssignmentEnabled={isAssignmentEnabled}
          />
          <VStack
            w="100%"
            h="100%"
            flexDir="column"
            p={`${HEADER_HEIGHT + 64}px 64px`}
            spacing="64px"
            alignItems="flex-start"
          >
            <VStack spacing="16px" alignItems="flex-start">
              <Heading as="h1">Calculation Error</Heading>
              <Text>{`We encountered ${
                errors.length > 0 ? 'errors' : 'an error'
              } while recalculating the ${
                isPreview ? 'preview' : 'portfolio'
              }. Contact your Finley Customer Service Manager for support.`}</Text>
            </VStack>
            <VStack spacing="16px">
              {errors.map((error) => (
                <Alert key={error} status="error">
                  <Text>{error}</Text>
                </Alert>
              ))}
            </VStack>
          </VStack>
        </CreditFacilityProvider>
      </Flex>
    </>
  );
};
